import clsx from "clsx";
import {SubmitButton} from "components/Button";
import {FormCard} from "components/Form";
import {DashboardLayout} from "components/Layout";
import {CircleLoader} from "components/Loader";
import {ModalConfirmation} from "components/Modal";
import {FieldArray, Formik} from "formik";
import {useUploadFile} from "hooks/Misc";
import {
	useAddVendorPIC,
	useDeleteVendorPIC,
	useDetailVendor,
	useEditVendor,
	useEditVendorPIC,
	useListVendorPIC,
} from "hooks/SuperAdmin/Vendor";
import {useStateCallback} from "hooks/useStateCallback";
import {useToast} from "hooks/useToast";
import {useEffect, useRef, useState} from "react";
import PhoneInput from "react-phone-number-input";
import {useHistory, useParams} from "react-router-dom";
import {convertErrorMessageFormat} from "utils/converter";
import {loadLocalAssets} from "utils/loader";
import * as Yup from "yup";

const SUPPORTED_FORMATS = ["image/jpg", "image/jpeg", "image/gif", "image/png"];
const FILE_SIZE = 5242880; // 5MB

const FormSchemaValidation = Yup.object().shape({
	vendorCode: Yup.string(),
	vendorName: Yup.string().required("Nama vendor wajib di isi"),
	email: Yup.string().email("Email yang dimasukkan harus sesuai dengan format yang valid"),
	phoneNumber: Yup.string()
		.max(15, "Nomor Telepon maximal 15 digit")
		.test("inputLength", "Nomor Telepon minimal 9 digit", value => {
			if (!value) return true;

			return !!value && (value.length === 2 || value.length >= 9);
		}),
	icon: Yup.mixed()
		.test("fileFormat", "Format logo tidak support, hanya menerima format .JPG .JPEG .PNG", value => {
			if (value === undefined || value === "" || value === null) return true;
			if (typeof value === "string") {
				return true;
			} else {
				return SUPPORTED_FORMATS.includes(value?.type);
			}
		})
		.test("fileSize", "Ukuran file untuk logo maksimal 5 MB", value => {
			if (value === undefined || value === "" || value === null) return true;
			if (typeof value === "string") {
				return true;
			} else {
				return value?.size <= FILE_SIZE;
			}
		})
		.nullable(),
	pic: Yup.array().of(
		Yup.object().shape({
			id: Yup.number().nullable(),
			name: Yup.string().required("Nama PIC wajib di isi"),
			email: Yup.string()
				.email("Email yang dimasukkan harus sesuai dengan format yang valid")
				.required("Email wajib di isi"),
			phoneNumber: Yup.string().required("Nomor telepon wajib di isi").min(9).max(15),
		}),
	),
});

const FormInitialValues = {vendorCode: "", vendorName: "", email: "", phoneNumber: "62", icon: undefined, pic: []};
const DEFAULT_PIC = {id: null, name: "", email: "", phoneNumber: "62"};

const MasterVendorEditPage = () => {
	const formikRef = useRef();
	const {showToast} = useToast();
	const history = useHistory();
	const {id} = useParams();
	const {data: detailRole, isLoading: isFetching} = useDetailVendor(id);
	const {data: picList} = useListVendorPIC({page: 1, limit: 9999, search: ""}, id);

	const [logoPreview, setLogoPreview] = useState(null);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [deleteItemData, setDeleteItemData] = useStateCallback({id: null, name: null, idx: null});

	const {mutate, isLoading} = useEditVendor();
	const {mutate: uploadFile} = useUploadFile();
	const {mutateAsync: picAdd} = useAddVendorPIC();
	const {mutateAsync: picEdit} = useEditVendorPIC();
	const {mutate: deleteVendorPICById, isLoading: isDeletingData} = useDeleteVendorPIC();

	const handleEnd = (values, pic) => {
		mutate(values, {
			onSuccess: async () => {
				for (const i of pic) {
					const {id: picId, ...rest} = i;

					if (i.id !== null) {
						await picEdit({id, picId, formData: rest});
					} else {
						await picAdd({id, formData: rest});
					}
				}

				showToast("success", "Data berhasil diubah", 3000);
				history.push("/master-vendor");
			},
			onError: res => {
				showToast("error", convertErrorMessageFormat(res.response.status, res.response.data.message), null);
			},
		});
	};

	const handleSubmit = values => {
		const {icon, phoneNumber, pic, vendorCode, ...rest} = values;

		if (icon === undefined || icon === "" || icon === null || typeof icon === "string") {
			handleEnd({id, icon: null, phoneNumber: phoneNumber.length > 2 ? phoneNumber : "", ...rest}, pic);
		} else {
			let formData = new FormData();

			uploadFile(formData, {
				onSuccess: result => {
					handleEnd(
						{
							id,
							icon: result.data?.file,
							phoneNumber: phoneNumber.length > 2 ? phoneNumber : "",
							...rest,
						},
						pic,
					);
				},
			});

			formData.append("file", icon);
		}
	};

	useEffect(() => {
		formikRef.current?.setFieldValue("vendorCode", detailRole?.data?.code);
		formikRef.current?.setFieldValue("vendorName", detailRole?.data?.name);
		formikRef.current?.setFieldValue("email", detailRole?.data?.email);
		formikRef.current?.setFieldValue("phoneNumber", detailRole?.data?.phoneNumber);
		formikRef.current?.setFieldValue("icon", detailRole?.data?.icon);
		setLogoPreview(detailRole?.data?.icon);

		if (picList && picList.data.rows.length > 0) {
			for (let i = 0; i < picList.data.rows.length; i++) {
				const pic = picList.data.rows[i];

				formikRef.current?.setFieldValue(`pic.${i}.id`, pic.id);
				formikRef.current?.setFieldValue(`pic.${i}.name`, `${pic.firstName} ${pic.lastName}`);
				formikRef.current?.setFieldValue(`pic.${i}.email`, pic.email);
				formikRef.current?.setFieldValue(`pic.${i}.phoneNumber`, pic.phoneNumber);
			}
		}
	}, [detailRole, picList]);

	if (isFetching) {
		return (
			<DashboardLayout>
				<FormCard title="Edit Vendor">
					<CircleLoader />
				</FormCard>
			</DashboardLayout>
		);
	}

	const handleUploadLogo = e => {
		const file = e.target.files[0];
		const reader = new FileReader();

		if (!file) return;
		if (!file.type.includes("image")) {
			formikRef?.current?.setFieldError(
				"icon",
				"Format logo tidak support, hanya menerima format .JPG .JPEG .PNG",
			);

			return;
		}
		if (file.size > 5242880) {
			formikRef?.current?.setFieldError("icon", "Ukuran file untuk logo maksimal 5 MB");

			return;
		}

		reader.onload = () => {
			setLogoPreview(reader.result);
		};
		reader.readAsDataURL(file);
		formikRef.current.setFieldValue("icon", file);
	};

	const handleDeleteData = remove => {
		deleteVendorPICById(
			{vendorId: id, picId: deleteItemData.id},
			{
				onSuccess: () => {
					showToast(
						"success",
						`Data PIC "${deleteItemData.firstName} ${deleteItemData.lastName}" berhasil dihapus`,
						3000,
					);
					remove(deleteItemData.idx);
					setShowDeleteModal(false);
				},
				onError: res => {
					showToast("error", convertErrorMessageFormat(res.response.status, res.response.data.message), null);
				},
			},
		);
	};

	return (
		<DashboardLayout>
			<Formik
				initialValues={FormInitialValues}
				innerRef={formikRef}
				validationSchema={FormSchemaValidation}
				onSubmit={handleSubmit}>
				{({handleSubmit, handleBlur, handleChange, values, errors, touched, setFieldValue}) => (
					<FormCard
						backTitle="Daftar list vendor"
						title="Edit Vendor"
						onBackButtonClick={() => history.push("/master-vendor")}>
						<div class="row gx-2 mb-5">
							<div class="col-sm-6 col-6">
								<div class="setting-item">
									<label class="setting-label required">Kode Vendor</label>
									<input
										class={clsx(
											"form-control",
											touched.vendorCode && errors?.vendorCode && "form-control-error",
										)}
										disabled={true}
										name="vendorCode"
										placeholder=""
										type="text"
										value={values?.vendorCode}
										onBlur={handleBlur}
										onChange={handleChange}
									/>
									{touched.vendorCode && errors.vendorCode && (
										<p className="form-error-item-message">{errors.vendorCode}</p>
									)}
								</div>
							</div>
							<div class="col-sm-6 col-6">
								<div class="setting-item">
									<label class="setting-label required">Nama Vendor</label>
									<input
										class={clsx(
											"form-control",
											touched.vendorName && errors?.vendorName && "form-control-error",
										)}
										disabled={isLoading}
										name="vendorName"
										placeholder=""
										type="text"
										value={values?.vendorName}
										onBlur={handleBlur}
										onChange={handleChange}
									/>
									{touched.vendorName && errors.vendorName && (
										<p className="form-error-item-message">{errors.vendorName}</p>
									)}
								</div>
							</div>
							<div class="col-sm-6 col-6">
								<div class="setting-item">
									<label class="setting-label">Email</label>
									<input
										class={clsx(
											"form-control",
											touched.email && errors?.email && "form-control-error",
										)}
										disabled={isLoading}
										name="email"
										placeholder=""
										type="text"
										value={values?.email}
										onBlur={handleBlur}
										onChange={handleChange}
									/>
									{touched.email && errors.email && (
										<p className="form-error-item-message">{errors.email}</p>
									)}
								</div>
							</div>
							<div class="col-sm-6 col-6">
								<div class="setting-item">
									<label class="setting-label">No Telepon</label>
									<PhoneInput
										className={clsx(
											touched?.phoneNumber && errors?.phoneNumber && "form-control-error",
										)}
										countryCallingCodeEditable={false}
										defaultCountry="ID"
										international={true}
										name="phoneNumber"
										placeholder="Nomor Telepon"
										value={`+${values.phoneNumber}`}
										onBlur={handleBlur}
										onChange={val => {
											setFieldValue("phoneNumber", val?.replace(/\D/g, ""));
										}}
									/>
									{errors.phoneNumber && touched?.phoneNumber && (
										<p className="form-error-item-message">{errors.phoneNumber}</p>
									)}
								</div>
							</div>
							<div class="col-sm-6 col-6">
								<div class="setting-item">
									<label class="setting-label">Logo</label>
									<div class="change-img mb-4">
										<div class="for-img-change">
											<div class="bx-img-change">
												<img
													alt="Logo Rumah Sakit"
													class="img-fluid"
													src={
														logoPreview
															? logoPreview
															: loadLocalAssets("img/user-default.jpg")
													}
												/>
											</div>
										</div>
										<div class="for-info-change">
											<div class="upload-img-patient">
												<input id="upload" type="file" onChange={handleUploadLogo} />
												<label class="btn-upload-img-patient" for="upload">
													<img
														alt="Upload logo"
														class="img-fluid me-2"
														src={loadLocalAssets("img/icn-cam.svg")}
													/>
													Upload logo
												</label>
											</div>
											<div class="ft-12 text-grey">*Ukuran file: maks 5 Mb (.JPG .JPEG .PNG)</div>
										</div>
									</div>
									{errors?.icon && <p className="form-error-item-message">{errors?.icon}</p>}
								</div>
							</div>
						</div>

						<div class="row gx-2 mb-5">
							<FieldArray name="pic">
								{({remove, push}) => (
									<>
										<ModalConfirmation
											description={`Apakah anda yakin ingin menghapus data PIC "${deleteItemData.name}"?`}
											loading={isDeletingData}
											visible={showDeleteModal}
											onApprove={() => handleDeleteData(remove)}
											onClose={() => setShowDeleteModal(false)}
											onReject={() => setShowDeleteModal(false)}
										/>
										<div className="col-12 d-flex align-items-center justify-content-between">
											<div className="text-bold">PIC</div>
											{values?.pic.length === 0 ? (
												<a
													className={clsx("text-orange wait-pay")}
													style={{cursor: "pointer"}}
													onClick={() => {
														push(DEFAULT_PIC);
													}}>
													<span className="icon-ico-plus ft-18" />
												</a>
											) : null}
										</div>
										{values.pic.map((item, idx) => {
											return (
												<>
													<div className="col-12 col-lg-6">
														<div className="setting-item">
															<label class="setting-label required">Nama PIC</label>
															<input
																class={clsx(
																	"form-control",
																	touched?.pic?.[idx]?.name &&
																		errors?.pic?.[idx]?.name &&
																		"form-control-error",
																)}
																disabled={isLoading}
																name={`pic.${idx}.name`}
																placeholder=""
																type="text"
																value={values?.pic?.[idx]?.name}
																onBlur={handleBlur}
																onChange={handleChange}
															/>
															{touched?.pic?.[idx]?.name && errors?.pic?.[idx]?.name && (
																<p className="form-error-item-message">
																	{errors?.pic?.[idx]?.name}
																</p>
															)}
														</div>
													</div>
													<div className="col-12 col-lg-6">
														<div className="setting-item">
															<label class="setting-label required">Email</label>
															<input
																class={clsx(
																	"form-control",
																	touched?.pic?.[idx]?.email &&
																		errors?.pic?.[idx]?.email &&
																		"form-control-error",
																)}
																disabled={isLoading}
																name={`pic.${idx}.email`}
																placeholder=""
																type="text"
																value={values?.pic?.[idx]?.email}
																onBlur={handleBlur}
																onChange={handleChange}
															/>
															{touched?.pic?.[idx]?.email &&
																errors?.pic?.[idx]?.email && (
																	<p className="form-error-item-message">
																		{errors?.pic?.[idx]?.email}
																	</p>
																)}
														</div>
													</div>
													<div className="col-12 col-lg-6">
														<div className="setting-item">
															<label class="setting-label required">No Telepon</label>
															<PhoneInput
																className={clsx(
																	touched?.pic?.[idx]?.phoneNumber &&
																		errors?.pic?.[idx]?.phoneNumber &&
																		"form-control-error",
																)}
																countryCallingCodeEditable={false}
																defaultCountry="ID"
																international={true}
																name={`pic.${idx}.phoneNumber`}
																placeholder="Nomor Telepon"
																value={`+${values?.pic?.[idx]?.phoneNumber}`}
																onBlur={handleBlur}
																onChange={val => {
																	setFieldValue(
																		`pic.${idx}.phoneNumber`,
																		val?.replace(/\D/g, ""),
																	);
																}}
															/>
															{errors?.pic?.[idx]?.phoneNumber &&
																touched?.pic?.[idx]?.phoneNumber && (
																	<p className="form-error-item-message">
																		{errors?.pic?.[idx]?.phoneNumber}
																	</p>
																)}
														</div>
													</div>
													<div className="col-1 d-flex align-items-center">
														{idx === values?.pic.length - 1 ? (
															<a
																className={clsx("text-orange wait-pay mt-3")}
																style={{cursor: "pointer"}}
																onClick={() => {
																	push(DEFAULT_PIC);
																}}>
																<span className="icon-ico-plus ft-18" />
															</a>
														) : null}
														<a
															className={clsx("text-orange wait-pay mt-3")}
															style={{cursor: "pointer"}}
															onClick={() => {
																if (item.id !== null) {
																	setDeleteItemData({idx, ...item}, () =>
																		setShowDeleteModal(true),
																	);
																} else {
																	remove(idx);
																}
															}}>
															<span className="icon-ico-delete ft-18" />
														</a>
													</div>
												</>
											);
										})}
									</>
								)}
							</FieldArray>
						</div>

						<div class="text-center my-3">
							<SubmitButton
								className="btn btn-orange d-inline-block mw-250"
								disabled={!FormSchemaValidation.isValidSync(values)}
								loading={isLoading}
								text="Save"
								onClick={handleSubmit}
							/>
						</div>
					</FormCard>
				)}
			</Formik>
		</DashboardLayout>
	);
};

export {MasterVendorEditPage};
